import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';

const FOCUSABLE =
  'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * TEEB menu drawer: a modal <dialog> with sliding sub-panels.
 *
 * - The dialog gives us an inert background, a backdrop and Esc (via `cancel`).
 * - Tab is additionally kept inside the drawer so focus never leaves to the browser chrome.
 * - Sub-panels form a stack; Back pops it and returns focus to the item that opened it.
 */
class TeebMenuDrawer extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  toggleButton = null;
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /** @type {{ panel: HTMLElement, trigger: HTMLElement }[]} */
  #stack = [];
  #closing = false;
  #restoreFocus = true;
  #openedByEditor = false;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.toggleButton = this.querySelector('[data-teeb-drawer-open]');
    this.dialog = this.querySelector('dialog');
    if (!this.toggleButton || !this.dialog) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.toggleButton.addEventListener('click', () => this.open(), { signal });
    this.dialog.addEventListener('click', this.#onClick, { signal });
    this.dialog.addEventListener('cancel', this.#onCancel, { signal });
    this.dialog.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onEditorDeselect, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.dialog?.open) this.dialog.close();
    unlockScroll(this);
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  open() {
    const { dialog, toggleButton } = this;
    if (!dialog || !toggleButton || dialog.open) return;

    this.#resetPanels();
    dialog.showModal();
    lockScroll(this);
    toggleButton.setAttribute('aria-expanded', 'true');

    // Force a style flush so the slide-in transition runs from the closed position.
    void dialog.offsetWidth;
    dialog.classList.add('is-open');
  }

  close() {
    const { dialog, toggleButton } = this;
    if (!dialog || !toggleButton || !dialog.open || this.#closing) return;

    this.#closing = true;
    this.#openedByEditor = false;
    dialog.classList.remove('is-open');

    const finish = () => {
      dialog.close();
      unlockScroll(this);
      toggleButton.setAttribute('aria-expanded', 'false');
      if (this.#restoreFocus) toggleButton.focus({ preventScroll: true });
      this.#restoreFocus = true;
      this.#closing = false;
    };

    if (prefersReducedMotion()) {
      finish();
      return;
    }

    let done = false;
    const onEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.target !== dialog || event.propertyName !== 'transform' || done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      finish();
    };
    dialog.addEventListener('transitionend', onEnd);
    // Fallback in case transitionend never fires (e.g. the element was hidden mid-transition).
    setTimeout(() => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      finish();
    }, 700);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Clicks on the ::backdrop are dispatched to the dialog itself.
    if (target === this.dialog) {
      this.close();
      return;
    }

    if (target.closest('[data-teeb-drawer-close]')) {
      this.close();
      return;
    }

    // Search and cart reuse the header's own controls so their behaviour stays in one place.
    if (target.closest('[data-teeb-drawer-search]')) {
      this.#handOff('search-button button');
      return;
    }

    const cartLink = target.closest('[data-teeb-drawer-cart]');
    if (cartLink) {
      if (this.#handOff('[on\\:click^="#cart-drawer"]')) event.preventDefault();
      return;
    }

    const opener = /** @type {HTMLElement | null} */ (target.closest('[data-teeb-panel-open]'));
    if (opener) {
      this.#openPanel(opener);
      return;
    }

    if (target.closest('[data-teeb-panel-back]')) {
      this.#back();
    }
  };

  /**
   * Close the drawer, then activate a header control once the overlay has gone.
   * @param {string} selector - Selector of the header control to activate
   * @returns {boolean} Whether a header control was found
   */
  #handOff(selector) {
    const control = /** @type {HTMLElement | null} */ (
      [...document.querySelectorAll(selector)].find((el) => !this.contains(el))
    );
    if (!control || !this.dialog) return false;

    this.#restoreFocus = false;
    this.close();
    const delay = prefersReducedMotion() ? 0 : 450;
    setTimeout(() => control.click(), delay);
    return true;
  }

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Tab' || !this.dialog) return;

    const focusables = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => this.#isVisible(el));
    if (focusables.length === 0) return;

    const first = /** @type {HTMLElement} */ (focusables[0]);
    const last = /** @type {HTMLElement} */ (focusables[focusables.length - 1]);

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    if (!(event.target instanceof Node) || !this.dialog?.contains(event.target)) return;
    if (!this.isOpen) {
      this.open();
      this.#openedByEditor = true;
    }
  };

  #onEditorDeselect = () => {
    if (this.#openedByEditor) this.close();
  };

  /** @param {HTMLElement} opener */
  #openPanel(opener) {
    const id = opener.getAttribute('data-teeb-panel-open');
    const next = id ? this.dialog?.querySelector(`#${CSS.escape(id)}`) : null;
    const current = this.#activePanel();
    if (!(next instanceof HTMLElement) || !current) return;

    current.hidden = true;
    next.hidden = false;
    opener.setAttribute('aria-expanded', 'true');
    this.#stack.push({ panel: current, trigger: opener });
    this.#setDepth();
    this.#animate(next, 'forward');
    this.#scrollToTop();

    next.querySelector('[data-teeb-panel-back]')?.focus({ preventScroll: true });
  }

  #back() {
    const entry = this.#stack.pop();
    const current = this.#activePanel();
    if (!entry || !current) return;

    current.hidden = true;
    entry.panel.hidden = false;
    entry.trigger.setAttribute('aria-expanded', 'false');
    this.#setDepth();
    this.#animate(entry.panel, 'back');
    entry.trigger.focus({ preventScroll: true });
  }

  #resetPanels() {
    if (!this.dialog) return;

    for (const panel of this.dialog.querySelectorAll('[data-teeb-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = !panel.hasAttribute('data-teeb-panel-root');
    }
    for (const opener of this.dialog.querySelectorAll('[data-teeb-panel-open]')) {
      opener.setAttribute('aria-expanded', 'false');
    }
    this.#stack = [];
    this.#setDepth();
  }

  /** @returns {HTMLElement | null} */
  #activePanel() {
    return this.dialog?.querySelector('[data-teeb-panel]:not([hidden])') ?? null;
  }

  #setDepth() {
    if (this.dialog) this.dialog.dataset.depth = String(this.#stack.length);
  }

  #scrollToTop() {
    for (const el of this.dialog?.querySelectorAll('.teeb-drawer__inner, .teeb-drawer__main') ?? []) {
      el.scrollTop = 0;
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {'forward' | 'back'} direction
   */
  #animate(panel, direction) {
    if (prefersReducedMotion()) return;

    panel.classList.remove('is-entering-forward', 'is-entering-back');
    void panel.offsetWidth;
    panel.classList.add(`is-entering-${direction}`);
    panel.addEventListener(
      'animationend',
      () => panel.classList.remove('is-entering-forward', 'is-entering-back'),
      { once: true }
    );
  }

  /** @param {Element} el */
  #isVisible(el) {
    if (typeof el.checkVisibility === 'function') return el.checkVisibility();
    return el instanceof HTMLElement && el.offsetParent !== null;
  }
}

if (!customElements.get('teeb-menu-drawer')) {
  customElements.define('teeb-menu-drawer', TeebMenuDrawer);
}
